import { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe2, ArrowRightLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CurrencyBridge() {
  const [rates, setRates] = useState(null);
  const [amount, setAmount] = useState(100);
  const [fromCur, setFromCur] = useState('USD');
  const [toCur, setToCur] = useState('NGN');
  const [result, setResult] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/fx/rates`).then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const swap = () => { setFromCur(toCur); setToCur(fromCur); };

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await axios.post(`${API}/fx/convert`, {
        amount: Number(amount), from_currency: fromCur, to_currency: toCur,
      });
      setResult(data);
    } catch {} finally { setConverting(false); }
  };

  useEffect(() => { if (amount > 0) convert(); /* eslint-disable-next-line */ }, [amount, fromCur, toCur]);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Globe2 size={14} strokeWidth={1.5} /> Cross-Theater Currency Bridge
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Lagos ↔<br />
            <span className="font-display italic text-[#D4AF37]">Honolulu.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            One bridge. Two currencies. Every cross-theater settlement quotes USD baseline
            with a sovereign 75bps corridor spread — auditable, predictable, sealed.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-7">
          <div className="sov-border bg-[#0A0A0A] p-8 md:p-10">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Settlement Quote</div>
            <div className="grid grid-cols-12 gap-4 items-end">
              <div className="col-span-12 md:col-span-5">
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">From</label>
                <div className="flex items-center gap-3">
                  <select className="input-sovereign cursor-pointer flex-shrink-0 w-24" value={fromCur} onChange={(e) => setFromCur(e.target.value)}>
                    <option>USD</option><option>NGN</option>
                  </select>
                  <input type="number" min="0" className="input-sovereign flex-1 text-right font-display text-2xl"
                    value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="fx-amount-input" />
                </div>
              </div>
              <div className="col-span-12 md:col-span-2 text-center">
                <button onClick={swap} className="p-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors" data-testid="fx-swap">
                  <ArrowRightLeft size={16} strokeWidth={1.5} />
                </button>
              </div>
              <div className="col-span-12 md:col-span-5">
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">To</label>
                <div className="flex items-center gap-3">
                  <select className="input-sovereign cursor-pointer flex-shrink-0 w-24" value={toCur} onChange={(e) => setToCur(e.target.value)}>
                    <option>NGN</option><option>USD</option>
                  </select>
                  <div className="input-sovereign flex-1 text-right font-display text-2xl text-[#D4AF37] py-3.5" data-testid="fx-result">
                    {converting ? <Loader2 size={20} className="animate-spin inline" /> : result ? result.to_amount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                  </div>
                </div>
              </div>
            </div>

            {result && (
              <div className="mt-8 pt-6 border-t border-[#D4AF37]/15 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Row l="Effective Rate" v={result.rate.toFixed(4)} />
                <Row l="Mid Rate" v={result.mid_rate.toFixed(4)} />
                <Row l="Corridor Spread" v={`${result.spread_applied_pct}%`} />
              </div>
            )}
          </div>
        </div>

        <aside className="md:col-span-5">
          <div className="sov-border bg-[#0A0A0A] p-8 sticky top-28">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Live Corridor Telemetry</div>
            {rates && (
              <div className="space-y-4">
                <div className="border-b border-[#D4AF37]/15 pb-4">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">NGN per USD</div>
                  <div className="font-display text-4xl text-[#D4AF37]">₦{rates.rates.NGN.toLocaleString()}</div>
                </div>
                <Row l="Corridor Spread" v={`${rates.spread_bps}bps`} />
                <Row l="Settlement Lane" v={rates.corridor} />
                <Row l="Doctrine" v="Auditable · Sealed" />
              </div>
            )}
            <div className="mt-8 text-xs text-[#A3A3A3] font-light leading-relaxed italic">
              "Cross-theater capital moves under one bridge, one admiral, one ledger."
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between border-b border-[#D4AF37]/15 pb-2 last:border-b-0">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{l}</span>
    <span className="font-light text-[#F5F5F5] tracking-wide">{v}</span>
  </div>
);
